// ============================================================================
// SPRAY CHART COMPONENT
// ============================================================================
// SVG-based baseball field spray chart showing batted ball distribution.
// Displays pull/center/oppo zones with hit counts and batting averages.
// Includes batted ball profile (GB%, FB%, LD%, popup%) and Statcast metrics
// (exit velo, launch angle, hard hit rate, barrel rate).
// ============================================================================

import React from 'react';
import { Box, Typography, Grid, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const SPRAY_COLORS = {
  pull: '#e74c3c',
  center: '#f39c12',
  oppo: '#3498db',
};

/**
 * SprayChart - Batted ball distribution + Statcast metrics + individual scatter dots
 * @param {Object} props
 * @param {Object} props.battedBall - Batted ball data object
 * @param {string} props.batterHand - 'R' or 'L' for field orientation label
 * @param {string} props.perspective - 'batter' or 'pitcher'
 * @param {Array}  props.rawEvents - Individual batted ball events for scatter plot
 * @param {Array}  props.parkDimensions - [LF, LF-alley, LCF, CF, RCF, RF-alley, RF] in feet (optional)
 * @param {string} props.parkName - Name of the park (optional, for display)
 */
const SprayChart = ({ battedBall = {}, batterHand = 'R', perspective = 'batter', rawEvents = [], parkDimensions = null, parkName = null, onDotClick }) => {
  const theme = useTheme();
  const [hoverDot, setHoverDot] = React.useState(null);
  const [hoverZone, setHoverZone] = React.useState(null);

  const total = battedBall.total || 0;
  const spray = battedBall.spray || { pull: { count: 0, hits: 0 }, center: { count: 0, hits: 0 }, oppo: { count: 0, hits: 0 } };

  const sprayTotal = (spray.pull?.count || 0) + (spray.center?.count || 0) + (spray.oppo?.count || 0);

  const sprayData = [
    {
      key: 'pull',
      label: 'Pull',
      count: spray.pull?.count || 0,
      hits: spray.pull?.hits || 0,
      pct: sprayTotal > 0 ? ((spray.pull?.count || 0) / sprayTotal * 100).toFixed(1) : 0,
      avg: spray.pull?.count > 0 ? ((spray.pull?.hits || 0) / spray.pull.count).toFixed(3) : '.000',
      color: SPRAY_COLORS.pull,
    },
    {
      key: 'center',
      label: 'Center',
      count: spray.center?.count || 0,
      hits: spray.center?.hits || 0,
      pct: sprayTotal > 0 ? ((spray.center?.count || 0) / sprayTotal * 100).toFixed(1) : 0,
      avg: spray.center?.count > 0 ? ((spray.center?.hits || 0) / spray.center.count).toFixed(3) : '.000',
      color: SPRAY_COLORS.center,
    },
    {
      key: 'oppo',
      label: 'Oppo',
      count: spray.oppo?.count || 0,
      hits: spray.oppo?.hits || 0,
      pct: sprayTotal > 0 ? ((spray.oppo?.count || 0) / sprayTotal * 100).toFixed(1) : 0,
      avg: spray.oppo?.count > 0 ? ((spray.oppo?.hits || 0) / spray.oppo.count).toFixed(3) : '.000',
      color: SPRAY_COLORS.oppo,
    },
  ];

  // Compute scatter dot positions from rawEvents
  const scatterDots = React.useMemo(() => {
    if (!rawEvents || rawEvents.length === 0) return [];
    return rawEvents.map((e, i) => {
      const rawEvent = e;
      // Convert spray angle + normalized distance to SVG x,y
      // Home plate is at (100, 140). Field goes up. Spray angle: -45=pull(L), 0=center, +45=oppo(R)
      const angleRad = ((e.sprayAngle || 0) * Math.PI) / 180;
      const maxR = 95; // max radius in SVG units
      const r = (e.normDist || 0.4) * maxR;
      const x = 100 + Math.sin(angleRad) * r;
      const y = 140 - Math.cos(angleRad) * r;
      // Color by result
      const result = (e.result || '').toLowerCase();
      let color;
      if (result.includes('home_run') || result === 'home run') color = '#e74c3c';
      else if (result === 'triple') color = '#9b59b6';
      else if (result === 'double') color = '#3498db';
      else if (result === 'single') color = '#2ecc71';
      else color = '#666'; // out
      const ev = e.exitVelocity || 0;
      const size = ev >= 100 ? 4.4 : ev >= 95 ? 3.8 : 3;
      return { x, y, color, size, ev, result, distance: e.distance, key: i, rawEvent };
    });
  }, [rawEvents]);

  const hasScatter = scatterDots.length > 0;

  // ── Park-specific outfield wall path ──
  // Converts [LF, LF-alley, LCF, CF, RCF, RF-alley, RF] distances (ft) to SVG path
  // Angles: -45°, -30°, -15°, 0°, +15°, +30°, +45° from center
  const WALL_ANGLES = [-45, -30, -15, 0, 15, 30, 45];
  const parkWallPath = React.useMemo(() => {
    if (!parkDimensions || parkDimensions.length !== 7) return null;
    const points = parkDimensions.map((dist, i) => {
      const angleRad = (WALL_ANGLES[i] * Math.PI) / 180;
      const r = (dist / 450) * 95;
      const x = 100 + Math.sin(angleRad) * r;
      const y = 140 - Math.cos(angleRad) * r;
      return { x, y, dist };
    });
    // Build smooth SVG path through points using quadratic bezier curves
    let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
    for (let i = 1; i < points.length; i++) {
      // Simple smooth curve segment
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2 - 2; // slight outward bulge
      d += ` Q${cpx.toFixed(1)},${cpy.toFixed(1)} ${curr.x.toFixed(1)},${curr.y.toFixed(1)}`;
    }
    return { d, points };
  }, [parkDimensions]);

  // Distance labels for park wall
  const wallLabels = React.useMemo(() => {
    if (!parkWallPath) return [];
    const { points } = parkWallPath;
    // Show distance at LF line, CF, RF line, and power alleys
    const labelIndices = [0, 1, 3, 5, 6]; // LF, LF-alley, CF, RF-alley, RF
    const labelNames = ['LF', '', 'CF', '', 'RF'];
    return labelIndices.map((idx, li) => {
      const p = points[idx];
      return { x: p.x, y: p.y, dist: p.dist, name: labelNames[li] };
    });
  }, [parkWallPath]);

  // Batted ball profile
  const profile = [
    { label: 'GB%', value: battedBall.groundBallRate || 0, count: battedBall.groundBall || 0, color: '#8d6e63' },
    { label: 'FB%', value: battedBall.flyBallRate || 0, count: battedBall.flyBall || 0, color: '#42a5f5' },
    { label: 'LD%', value: battedBall.lineDriveRate || 0, count: battedBall.lineDrive || 0, color: '#66bb6a' },
    { label: 'PU%', value: total > 0 ? ((battedBall.popup || 0) / total * 100).toFixed(1) : 0, count: battedBall.popup || 0, color: '#ef5350' },
  ];

  // Statcast metrics
  const statcast = [
    { label: 'Avg Exit Velo', value: battedBall.avgExitVelo ? `${battedBall.avgExitVelo} mph` : '—', highlight: battedBall.avgExitVelo >= 90 },
    { label: 'Avg Launch Angle', value: battedBall.avgLaunchAngle ? `${battedBall.avgLaunchAngle}°` : '—' },
    { label: 'Avg Distance', value: battedBall.avgDistance ? `${battedBall.avgDistance} ft` : '—' },
    { label: 'Hard Hit%', value: battedBall.hardHitRate ? `${battedBall.hardHitRate}%` : '—', highlight: battedBall.hardHitRate >= 40 },
    { label: 'Barrel%', value: battedBall.barrelRate ? `${battedBall.barrelRate}%` : '—', highlight: battedBall.barrelRate >= 8 },
  ];

  if (total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No batted ball data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Batted Ball Profile
        </Typography>
        <Chip label={`${total} balls in play`} size="small" variant="outlined" />
      </Box>

      <Grid container spacing={2}>
        {/* SVG Spray Chart with individual batted ball dots */}
        <Grid item xs={12} md={5}>
          <Box sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            p: 1, backgroundColor: alpha(theme.palette.success.main, 0.05),
            borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.3)}`
          }}>
            <svg viewBox="0 0 200 155" width="100%" style={{ maxWidth: 520 }}>
              {/* Field outline */}
              <path d="M100,140 L20,60 A110,110 0 0,1 180,60 Z" fill={alpha('#4caf50', 0.08)} stroke={alpha('#4caf50', 0.25)} strokeWidth="0.8" />

              {/* Faint zone dividers */}
              <line x1="100" y1="140" x2={batterHand === 'R' ? 60 : 140} y2="35" stroke={alpha('#fff', 0.08)} strokeWidth="0.5" strokeDasharray="3,3" />
              <line x1="100" y1="140" x2={batterHand === 'R' ? 140 : 60} y2="35" stroke={alpha('#fff', 0.08)} strokeWidth="0.5" strokeDasharray="3,3" />

              {/* Distance arcs at 150ft, 300ft, 400ft */}
              <path d={`M${100 - 33},${140 - 28} A37,37 0 0,1 ${100 + 33},${140 - 28}`} fill="none" stroke={alpha('#fff', 0.06)} strokeWidth="0.5" />
              <path d={`M${100 - 63},${140 - 55} A70,70 0 0,1 ${100 + 63},${140 - 55}`} fill="none" stroke={alpha('#fff', 0.06)} strokeWidth="0.5" />
              <path d={`M${100 - 82},${140 - 75} A95,95 0 0,1 ${100 + 82},${140 - 75}`} fill="none" stroke={alpha('#fff', 0.06)} strokeWidth="0.5" />

              {/* Infield diamond */}
              <polygon points="100,116 116,105 100,94 84,105" fill="none" stroke={alpha('#8d6e63', 0.3)} strokeWidth="0.5" />

              {/* Park-specific outfield wall */}
              {parkWallPath && (
                <>
                  {/* Wall outline */}
                  <path d={parkWallPath.d} fill="none" stroke="#ffa726" strokeWidth="1.8" opacity="0.85" strokeLinecap="round" strokeLinejoin="round" />
                  {/* Foul lines from home plate to wall corners */}
                  <line x1="100" y1="140" x2={parkWallPath.points[0].x} y2={parkWallPath.points[0].y}
                    stroke="#ffa726" strokeWidth="0.6" opacity="0.4" strokeDasharray="2,2" />
                  <line x1="100" y1="140" x2={parkWallPath.points[6].x} y2={parkWallPath.points[6].y}
                    stroke="#ffa726" strokeWidth="0.6" opacity="0.4" strokeDasharray="2,2" />
                  {/* Distance labels along the wall */}
                  {wallLabels.map((lbl, i) => (
                    <g key={i}>
                      <text x={lbl.x} y={lbl.y - 3} fill="#ffa726" fontSize="5" fontWeight="bold"
                        textAnchor="middle" opacity="0.9">
                        {lbl.dist}'
                      </text>
                      {lbl.name && (
                        <text x={lbl.x} y={lbl.y + 4} fill="#ffa726" fontSize="4"
                          textAnchor="middle" opacity="0.6">
                          {lbl.name}
                        </text>
                      )}
                    </g>
                  ))}
                  {/* Park name label */}
                  {parkName && (
                    <text x="100" y="10" fill="#ffa726" fontSize="5.5" fontWeight="bold"
                      textAnchor="middle" opacity="0.7">
                      {parkName}
                    </text>
                  )}
                </>
              )}

              {/* Individual batted ball dots — state-driven hover overlay (avoids
                  MUI Tooltip swallowing SVG click events). */}
              {hasScatter ? scatterDots.map(dot => (
                <circle
                  key={dot.key}
                  cx={dot.x} cy={dot.y} r={dot.size}
                  fill={dot.color}
                  opacity={hoverDot?.key === dot.key ? 1 : 0.85}
                  stroke={hoverDot?.key === dot.key ? '#fff' : (onDotClick ? alpha('#fff', 0.7) : alpha('#000', 0.3))}
                  strokeWidth={hoverDot?.key === dot.key ? 1.2 : (onDotClick ? 0.6 : 0.3)}
                  style={{ cursor: onDotClick ? 'pointer' : 'default', transition: 'opacity 0.1s' }}
                  onClick={onDotClick ? (e) => { e.stopPropagation(); onDotClick(dot.rawEvent); } : undefined}
                  onMouseEnter={() => setHoverDot(dot)}
                  onMouseLeave={() => setHoverDot(prev => prev?.key === dot.key ? null : prev)}
                />
              )) : (
                <>
                  {/* Fallback: zone heatmap when no individual events */}
                  <path
                    d={batterHand === 'R' ? "M100,140 L20,60 A110,110 0 0,1 60,35 L100,140 Z" : "M100,140 L140,35 A110,110 0 0,1 180,60 L100,140 Z"}
                    fill={alpha(SPRAY_COLORS.pull, 0.15 + (spray.pull?.count || 0) / Math.max(sprayTotal, 1) * 0.4)}
                    stroke={SPRAY_COLORS.pull} strokeWidth={hoverZone === 'pull' ? 2.5 : 1.5}
                    opacity={hoverZone === 'pull' ? 1 : 0.8}
                    style={{ cursor: 'pointer', transition: 'stroke-width 0.1s' }}
                    onMouseEnter={() => setHoverZone('pull')}
                    onMouseLeave={() => setHoverZone(prev => prev === 'pull' ? null : prev)}
                  />
                  <path d="M100,140 L60,35 A110,110 0 0,1 140,35 L100,140 Z"
                    fill={alpha(SPRAY_COLORS.center, 0.15 + (spray.center?.count || 0) / Math.max(sprayTotal, 1) * 0.4)}
                    stroke={SPRAY_COLORS.center} strokeWidth={hoverZone === 'center' ? 2.5 : 1.5}
                    opacity={hoverZone === 'center' ? 1 : 0.8}
                    style={{ cursor: 'pointer', transition: 'stroke-width 0.1s' }}
                    onMouseEnter={() => setHoverZone('center')}
                    onMouseLeave={() => setHoverZone(prev => prev === 'center' ? null : prev)}
                  />
                  <path
                    d={batterHand === 'R' ? "M100,140 L140,35 A110,110 0 0,1 180,60 L100,140 Z" : "M100,140 L20,60 A110,110 0 0,1 60,35 L100,140 Z"}
                    fill={alpha(SPRAY_COLORS.oppo, 0.15 + (spray.oppo?.count || 0) / Math.max(sprayTotal, 1) * 0.4)}
                    stroke={SPRAY_COLORS.oppo} strokeWidth={hoverZone === 'oppo' ? 2.5 : 1.5}
                    opacity={hoverZone === 'oppo' ? 1 : 0.8}
                    style={{ cursor: 'pointer', transition: 'stroke-width 0.1s' }}
                    onMouseEnter={() => setHoverZone('oppo')}
                    onMouseLeave={() => setHoverZone(prev => prev === 'oppo' ? null : prev)}
                  />
                </>
              )}

              {/* Home plate */}
              <polygon points="96,138 100,142 104,138 103,136 97,136" fill="#fff" stroke="#333" strokeWidth="0.5" />

              {/* Spray zone labels (hide center text when park name is shown) */}
              <text x={batterHand === 'R' ? 28 : 162} y="56" fill={SPRAY_COLORS.pull} fontSize="7" fontWeight="bold" textAnchor="middle" opacity="0.7">Pull</text>
              <text x={batterHand === 'R' ? 28 : 162} y="64" fill={SPRAY_COLORS.pull} fontSize="6" textAnchor="middle" opacity="0.6">{sprayData[0].pct}%</text>
              {!parkName && <text x="100" y="32" fill={SPRAY_COLORS.center} fontSize="7" fontWeight="bold" textAnchor="middle" opacity="0.7">Center</text>}
              {!parkName && <text x="100" y="40" fill={SPRAY_COLORS.center} fontSize="6" textAnchor="middle" opacity="0.6">{sprayData[1].pct}%</text>}
              <text x={batterHand === 'R' ? 162 : 28} y="56" fill={SPRAY_COLORS.oppo} fontSize="7" fontWeight="bold" textAnchor="middle" opacity="0.7">Oppo</text>
              <text x={batterHand === 'R' ? 162 : 28} y="64" fill={SPRAY_COLORS.oppo} fontSize="6" textAnchor="middle" opacity="0.6">{sprayData[2].pct}%</text>

              {/* Hover info overlay — drawn LAST so it sits above everything */}
              {hoverDot && (() => {
                const labelLines = [
                  `${(hoverDot.result || 'Out').toUpperCase()}`,
                  `${hoverDot.ev ? hoverDot.ev.toFixed(0) + ' mph' : '— mph'} · ${hoverDot.distance ? hoverDot.distance + ' ft' : '— ft'}`,
                  hoverDot.rawEvent?.gameDate ? hoverDot.rawEvent.gameDate : null,
                  hoverDot.rawEvent?.opponent ? 'vs ' + hoverDot.rawEvent.opponent : null,
                  onDotClick ? 'click for details' : null,
                ].filter(Boolean);
                const boxW = 60;
                const boxH = 4 + labelLines.length * 5.5;
                let bx = hoverDot.x + 5;
                let by = hoverDot.y - boxH - 4;
                if (bx + boxW > 198) bx = hoverDot.x - boxW - 5;
                if (by < 2) by = hoverDot.y + 5;
                return (
                  <g pointerEvents="none">
                    <rect x={bx} y={by} width={boxW} height={boxH} rx="1.5" ry="1.5"
                      fill={theme.palette.mode === 'dark' ? 'rgba(20,24,32,0.95)' : 'rgba(255,255,255,0.97)'}
                      stroke={hoverDot.color} strokeWidth="0.5" />
                    {labelLines.map((line, idx) => (
                      <text key={idx} x={bx + 3} y={by + 5 + idx * 5.5}
                        fill={theme.palette.mode === 'dark' ? '#fff' : '#222'}
                        fontSize="4" fontWeight={idx === 0 ? 'bold' : 'normal'}>
                        {line}
                      </text>
                    ))}
                  </g>
                );
              })()}
              {!hasScatter && hoverZone && (() => {
                const z = sprayData.find(d => d.key === hoverZone);
                if (!z) return null;
                const cx = hoverZone === 'pull' ? (batterHand === 'R' ? 50 : 150)
                        : hoverZone === 'oppo' ? (batterHand === 'R' ? 150 : 50)
                        : 100;
                const cy = 80;
                const boxW = 50, boxH = 22;
                let bx = cx - boxW / 2;
                let by = cy - boxH / 2;
                return (
                  <g pointerEvents="none">
                    <rect x={bx} y={by} width={boxW} height={boxH} rx="1.5" ry="1.5"
                      fill={theme.palette.mode === 'dark' ? 'rgba(20,24,32,0.95)' : 'rgba(255,255,255,0.97)'}
                      stroke={z.color} strokeWidth="0.5" />
                    <text x={bx + 3} y={by + 5} fill={z.color} fontSize="5" fontWeight="bold">{z.label.toUpperCase()}</text>
                    <text x={bx + 3} y={by + 11} fill={theme.palette.mode === 'dark' ? '#fff' : '#222'} fontSize="4">
                      {z.count} balls · {z.pct}%
                    </text>
                    <text x={bx + 3} y={by + 17} fill={theme.palette.mode === 'dark' ? '#fff' : '#222'} fontSize="4">
                      {z.hits} hits · AVG {z.avg}
                    </text>
                  </g>
                );
              })()}
            </svg>
          </Box>

          {/* Legend */}
          {hasScatter && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
              {[
                { label: 'HR', color: '#e74c3c' },
                { label: '3B', color: '#9b59b6' },
                { label: '2B', color: '#3498db' },
                { label: '1B', color: '#2ecc71' },
                { label: 'Out', color: '#666' },
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: item.color }} />
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Batted Ball Profile + Statcast */}
        <Grid item xs={12} md={7}>
          {/* Batted ball type distribution */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Batted Ball Types</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
            {profile.map(p => (
              <Box key={p.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ width: 32, fontWeight: 'bold', fontSize: '0.75rem' }}>{p.label}</Typography>
                <Box sx={{ flex: 1, height: 14, borderRadius: 1, overflow: 'hidden', backgroundColor: alpha(p.color, 0.1) }}>
                  <Box sx={{
                    width: `${Math.min(Number(p.value) || 0, 100)}%`,
                    height: '100%',
                    backgroundColor: alpha(p.color, 0.6),
                    borderRadius: 1,
                    transition: 'width 0.5s ease',
                  }} />
                </Box>
                <Typography variant="caption" sx={{ width: 40, textAlign: 'right', fontSize: '0.75rem' }}>{p.value}%</Typography>
                <Typography variant="caption" sx={{ width: 25, textAlign: 'right', opacity: 0.6, fontSize: '0.75rem' }}>({p.count})</Typography>
              </Box>
            ))}
          </Box>

          {/* Statcast metrics */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Statcast Metrics</Typography>
          <Grid container spacing={1}>
            {statcast.map(s => (
              <Grid item xs={6} sm={4} key={s.label}>
                <Box sx={{
                  p: 1, borderRadius: 1.5,
                  backgroundColor: s.highlight ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${s.highlight ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.divider, 0.2)}`,
                  textAlign: 'center',
                }}>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.7, fontSize: '0.75rem', mb: 0.25 }}>{s.label}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem', color: s.highlight ? theme.palette.success.main : 'inherit' }}>
                    {s.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SprayChart;
